/**
 * A wall's compass label — `North` / `East` / `South` / `West`.
 *
 * Extracted from `2d/ElevationRenderer` so the elevations preview strip and
 * anything else that names a wall to a reader agree on ONE convention. There
 * is no captured heading in a RoomPlan scan, so "north" here is a naming
 * convention rather than a bearing — which is exactly why it must have a
 * single definition: a wall the elevations strip calls "North Wall" has to be
 * the same wall the scan-changes list calls the north wall.
 *
 * The angle is read off the wall's NORMAL, and for a room wall — a
 * zero-thickness face whose normal points into the room — that lands the label
 * on the side the wall SITS on: a wall at the top of the plan (smaller z, see
 * the z convention in CLAUDE.md) faces +Z, and is called North.
 *
 * A structure wall has two faces and no inward side, so its label is only as
 * meaningful as its stored normal. Callers that have a room wall for the same
 * line should pass that one.
 */
import { getWallNormal } from './elevationViewerHelpers';

/** @returns {'North'|'East'|'South'|'West'|null} */
export const wallCardinalDirection = (wall) => {
  if (!wall) return null;
  // For grouped walls, use the first wall's normal.
  const wallToCheck = wall.isGrouped ? wall.groupedWalls?.[0] || wall : wall;
  const normal = getWallNormal(wallToCheck);
  if (!normal) return null;

  // Project the normal onto the XZ plane (ignore Y) and map to a quadrant:
  // +Z = North (0°), +X = East (90°), -Z = South (180°), -X = West (270°).
  const angle = Math.atan2(normal.x, normal.z);
  const degrees = ((angle * 180) / Math.PI + 360) % 360;

  if (degrees >= 315 || degrees < 45) return 'North';
  if (degrees >= 45 && degrees < 135) return 'East';
  if (degrees >= 135 && degrees < 225) return 'South';
  return 'West';
};

/**
 * `north wall`, or null for a wall whose facing the data does not say. A wall
 * with neither corners nor a rotation would be read as facing +Z by
 * `getWallNormal`'s default — a guess this would print as a fact. How a
 * panel names a wall to a reader: the click bubble, the Backsplash card.
 */
export const wallSideName = (wall) => {
  const oriented = wall?.polygonCorners?.length >= 3 || !!wall?.rotation;
  const side = oriented ? wallCardinalDirection(wall) : null;
  return side ? `${side.toLowerCase()} wall` : null;
};

/**
 * How a panel names one of a room's walls: `West wall`, else its number in
 * the room (`Wall 3`) when the data does not say which way it faces. The
 * Backsplash card's "Also changes" links and the elevation's band panel.
 */
export const wallDisplayName = (room, wallId) => {
  const walls = room?.walls || [];
  const i = walls.findIndex((w) => w.id === wallId);
  const side = wallSideName(walls[i]);
  return side ? side[0].toUpperCase() + side.slice(1) : `Wall ${i + 1}`;
};
